import 'reflect-metadata'

import { Exclude, Type } from 'class-transformer'
import { Subject, Subscriber } from 'rxjs'
import { v4 as nanoid } from 'uuid'

import { TableEventKind } from '../messaging'
import { GameEvent, GameEvents } from '../messaging/event/GameEvent'
import { Evented, VersionedEvent } from '../messaging/EventSourced'
import { defaultPlayerActionTimeQuickPlay, defaultPlayerActionTimeTournament, logger } from '../util'
import { randomIntFromInterval } from './BotPlayerKind'
import { GameState } from './states'
import { GameModes } from './states/GameMode'
import { GameModeTag } from './states/GameMode.types'
import { GameStates } from './states/GameState'
import { GameActions } from './states/GameStateAction'
import { GameReducer as GameStateReducer } from './states/GameStateReducer'
import { Table } from './Table'
import { TournamentDifficulty } from './TournamentDifficulty'
import { CardString } from 'pokersolver'

const isTestingWithJest = process.env.JEST_WORKER_ID !== undefined

export const HOW_TO_PLAY_MODULE_ID = 4612

export const PLAYER_STATS_CONFIG = {
  FETCH_START_DATE: new Date(2023, 8, 1, 0, 0, 0, 0)
}

export const GAME_CONFIG = {
  DELAY_AFTER: {
    deal: 1,
    flop: 1,
    turn: 1,
    river: 1
  },
  MIN_DELAY_AFTER_SHOWDOWN: 2,
  MAX_DELAY_AFTER_SHOWDOWN: 5,
  MAX_DELAY_AFTER_SHOWDOWN_THREE_CARD: 2,
  PER_PLAYER_DELAY_AFTER_SHOWDOWN: 0.5,
  DELAY_BETWEEN_HANDS: 6,
  DELAY_BETWEEN_HANDS_PRACTICE_MODE: 7,
  DELAY_BETWEEN_HANDS_THREE_CARD: 2,
  DELAY_BUY_IN: 30,
  DELAY_PLAYER_ACTION_TIMER_EXPIRES: 1, // used to hide the bet modal 1sec before the timer expires
  SHOULD_SENT_PLAYER_ACTION_CHATS: false,
  SINGLE_PLAYER_WAIT_TIME: 2, // use to detect for a single user waiting in communnity game
  DEFAULT_REBUY_AMOUNT_FOR_QUICK_GAME: 10000, //Default rebuy chips for quick default game,
  START_TIMER_DELAY: 60,
  THREE_CARD: {
    STARTING_CHIPS: 5000
  }
}

export const BOT_CONFIG = {
  // constants for rule 1
  MIN_PLAYERS_RULE_1: 1,
  MAX_PLAYERS_RULE_1: 4,
  BOTS_RULE_1: [0, 2, 4],
  // constants for rule 3
  MIN_PLAYERS_RULE_3: 2,
  MAX_PLAYERS_RULE_3: 5,
  // constants for rule 4
  MIN_PLAYERS_RULE_4: 6,
  MAX_PLAYERS_RULE_4: 7,
  // constants for rule 6
  MIN_PLAYERS_RULE_6: 2,
  MAX_PLAYERS_RULE_6: 5,
  PROD: {
    // Waiting times in minutes to add bots in prod
    WaitingTime: {
      Zero: 0.2,
      First: 2,
      Second: 5,
      Third: 5,
      Fourth: 7.5
    }
  },
  DEV: {
    // Waiting times n minutes to add bots in dev
    WaitingTime: {
      Zero: 0.2,
      First: 1,
      Second: 1,
      Third: 1,
      Fourth: 3.5
    }
  },
  ACTION_TIME: {
    tournament: {
      MIN_ACTION_TIME: 1,
      MAX_ACTION_TIME: 2
    },
    practice: {
      MIN_ACTION_TIME: 1,
      MAX_ACTION_TIME: 2
    },
    default: {
      MIN_ACTION_TIME: 1,
      MAX_ACTION_TIME: 2
    },
    threeCard: {
      MIN_ACTION_TIME: 1
    }
  },
  DELAY_JOINING_TIME: 10
}

type BotActionTime = {
  getBotActionTime: () => number
}

export const BOT_CONFIG_ACTION_TIME: Record<GameModeTag, BotActionTime> = {
  tournament: {
    getBotActionTime() {
      return BOT_CONFIG.ACTION_TIME.tournament.MIN_ACTION_TIME
    }
  },
  threeCard: {
    getBotActionTime() {
      return BOT_CONFIG.ACTION_TIME.threeCard.MIN_ACTION_TIME
    }
  },
  practice: {
    getBotActionTime() {
      return randomIntFromInterval(
        BOT_CONFIG.ACTION_TIME.practice.MIN_ACTION_TIME,
        BOT_CONFIG.ACTION_TIME.practice.MAX_ACTION_TIME
      )
    }
  },
  practiceTournament: {
    getBotActionTime() {
      return randomIntFromInterval(
        BOT_CONFIG.ACTION_TIME.practice.MIN_ACTION_TIME,
        BOT_CONFIG.ACTION_TIME.practice.MAX_ACTION_TIME
      )
    }
  },
  openQuickGame: {
    getBotActionTime() {
      return randomIntFromInterval(
        BOT_CONFIG.ACTION_TIME.practice.MIN_ACTION_TIME,
        BOT_CONFIG.ACTION_TIME.practice.MAX_ACTION_TIME
      )
    }
  },
  default: {
    getBotActionTime() {
      return BOT_CONFIG.ACTION_TIME.default.MIN_ACTION_TIME
    }
  }
}

type GameStartOptions = {
  mode: GameModeTag // GameModeTag
  blindTimerCountdown: number
  playerActionTime?: number | undefined
  // this should be required later on
  tournamentDifficulty?: TournamentDifficulty
  cards?: CardString[]
}

/**
 * Base class for all Poker games.
 */
export class Game extends Evented {
  @Type(() => Table)
  tables: Table[] = []
  @Type(() => GameState)
  state: GameState = GameStates.PreGame

  @Exclude()
  @Type(() => Subject)
  public event$ = new Subject<GameEvent>()

  @Type(() => Table)
  public get defaultTable(): Table {
    return this.tables[0]
  }

  // prettier-ignore
  constructor(
    public name: string,
    subscriber?: Subscriber<GameEvent>,
    id: string = nanoid()
  ) {
    super(id)

    const table = new Table(`${this.name}`)

    table.gameId = this.getId()
    table.gameName = this.name
    this.tables = [table]

    this.tables.forEach((t) => {
      t.event$.subscribe({
        next: (value) => {
          if (value.name === TableEventKind.Finished) {
            this.endGame()
          }
        }
      })
    })

    if (subscriber) {
      this.event$.subscribe(subscriber)
    }

    this.update(GameEvents.created(this))
  }

  startGame(option?: GameStartOptions) {
    logger.trace('[Game.startGame]', option)
    this.update(GameEvents.started(this))
    if (option) {
      const { mode, blindTimerCountdown, playerActionTime, cards } = option
      // start tables or deal new hands
      this.tables.map((table) => {
        table.mode = GameModes.returnMode(mode)
        logger.trace(`[Game.startGame] table mode ${table.mode}`)
        logger.trace(`[Game.startGame] table playerActionTime ${playerActionTime}`)
        if (playerActionTime) {
          table.playerActionTime = playerActionTime
        } else {
          if (!table.isPlaying()) {
            // Set player action time for open quick game
            if (GameModes.isOpenQuickGame(table.mode)) {
              table.playerActionTime = defaultPlayerActionTimeTournament * 1000
            } else {
              table.playerActionTime = GameModes.isTournament(table.mode)
                ? defaultPlayerActionTimeTournament * 1000
                : defaultPlayerActionTimeQuickPlay * 1000
            }
          }
        }
        if (GameModes.isTournament(table.mode)) {
          table.difficulty = option.tournamentDifficulty ? option.tournamentDifficulty : TournamentDifficulty.GENERAL
          table.blindTimerCountdown = blindTimerCountdown
          if (table.gameStartTime === 0) {
            table.gameStartTime = super.currentCheckpoint()
            table.blindTimerCheckpoint = table.gameStartTime + blindTimerCountdown * 60000
          }
        } else {
          if (table.gameStartTime === 0) {
            table.gameStartTime = super.currentCheckpoint()
          }
        }

        if (table.isPaused()) {
          table.unpause()
        }

        if (!table.isPlaying()) {
          if (GameModes.isThreeCard(table.mode) && cards) {
            table.threeCardDeck = cards
            table.amountOfHands = table.amountOfHands + 1
          }
          table.newHand()
        }
      })
    } else {
      this.tables.map((table) => {
        if (table.isPaused()) {
          table.unpause()
        }

        if (!table.isPlaying()) {
          table.newHand()
        }
      })
    }
    if (!isTestingWithJest) {
      this.event$.complete()
      // this.event$ = new Subject<GameEvent>()
    }
  }

  resumeGame(shouldEmitEvent = true) {
    //start tables if they are paused
    this.tables.forEach((table) => {
      if (table.isPaused()) {
        table.unpause()
        if (GameModes.isTournament(table.mode)) {
          table.blindTimerCheckpoint = super.currentCheckpoint() + (super.currentCheckpoint() - table.gamePauseTime)
          // table.gamePauseTime = 0
        }
      }
    })
    this.update(GameEvents.resumed(this))
    this.update(GameEvents.started(this))
    if (!isTestingWithJest && shouldEmitEvent) {
      this.event$.complete()
      // this.event$ = new Subject<GameEvent>()
    }
  }

  pauseGame(shouldEmitEvent = true, isSleeping = false) {
    // pause all tables
    this.tables.forEach((table) => {
      if (!table.isPaused()) {
        table.pause(shouldEmitEvent, isSleeping)
      }
    })
    this.update(GameEvents.paused(this))
    if (!isTestingWithJest) {
      this.event$.complete()
      // this.event$ = new Subject<GameEvent>()
    }
  }

  sleepGame() {
    // sleep all tables
    this.tables.map((table) => {
      if (!(table.isPaused() || table.isSleep())) {
        table.sleep()
      }
    })
    this.update(GameEvents.sleep(this))
    if (!isTestingWithJest) {
      this.event$.complete()
    }
  }

  endGame() {
    this.update(GameEvents.ended(this))
    if (!isTestingWithJest) {
      this.event$.complete()
      // this.event$ = new Subject<GameEvent>(
    }
  }

  onPokerGameCreated(event: GameEvent) {
    logger.trace('[Game.onPokerGameCreated] called')
    this.setId(event.snapshot.gameId)
    this.name = event.snapshot.gameName
    this.tables[0].setId(event.snapshot.table[0].id)
    this.state = GameStateReducer(GameActions.CreateGame, this.state)
  }

  onPokerGameStart() {
    logger.trace('[Game.onPokerGameStart] called')
    this.state = GameStateReducer(GameActions.StartPlay, this.state)
  }

  onPokerGamePause() {
    logger.trace('[Game.onPokerGamePause] called')
    this.state = GameStateReducer(GameActions.PausePlay, this.state)
  }

  onPokerGameResume() {
    logger.trace('[Game.onPokerGameResume] called')
  }

  onPokerGameSleep() {
    logger.trace('[Game.onPokerGamePause] called')
    this.state = GameStateReducer(GameActions.SleepPlay, this.state)
  }

  getTablesForPlayer(userId: string) {
    return this.tables.filter((table) => table.getPlayerByUserId(userId, true) !== undefined)
  }

  onPokerGameEnd() {
    logger.trace('[Game.onPokerGameEnd] called')
    this.state = GameStateReducer(GameActions.EndGame, this.state)
  }

  update(event: GameEvent) {
    // logger.trace('[Game.update]', event)
    //update internal state and append events
    this.updateState(event)

    //notify subscribers event happened
    this.event$.next(event)
  }

  getId(): string {
    return super.getId()
  }
  getVersion(): number {
    return super.getVersion()
  }
  getEvents(): VersionedEvent[] {
    return super.getEvents()
  }

  internalState() {
    return this.state.tag
  }
}
